import Page from 'attendee/theme/Page'
import React, {useEffect, useMemo, useState} from 'react'
import {LocalLink} from 'i18n/LocalLink'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'
import PricingIcon from 'assets/icons/svg/PricingIcon'
import styled from 'styled-components'
import {routes} from 'routes/routes'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'
import {fetchPublicEconomy} from 'platform/fetchPublicEconomy'

type MainTier = 'paid' | 'free' | 'charity'

const MAIN_LINE_COUNT: Record<MainTier, number> = {
  paid: 12,
  free: 12,
  charity: 12,
}

const USES_COUNT: Record<MainTier, number> = {
  paid: 8,
  free: 8,
  charity: 3,
}

export function Prices() {
  const {locale, t} = useI18n()
  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, routes.prices)}`

  const [perDayPaid, setPerDayPaid] = useState<number | null>(null)
  const [perDayFree, setPerDayFree] = useState<number | null>(null)

  useEffect(() => {
    fetchPublicEconomy()
      .then((e) => {
        setPerDayPaid(e.display_event_price_per_day)
        setPerDayFree(e.display_event_price_per_day)
      })
      .catch(() => {})
  }, [])

  const mainPlans = useMemo(
    () =>
      [
        {
          id: 1,
          price: perDayPaid,
          tier: 'paid' as MainTier,
          tierLabelKey: 'prices.tierPaid',
          pricingClass: 'pricing__item-label',
        },
        {
          id: 2,
          price: perDayFree,
          tier: 'free' as MainTier,
          tierLabelKey: 'prices.tierFree',
          pricingClass: 'pricing__item-label pricing__item-label-color1',
        },
        {
          id: 3,
          price: 0,
          tier: 'charity' as MainTier,
          tierLabelKey: 'prices.tierCharity',
          pricingClass: 'pricing__item-label pricing__item-label-color2',
        },
      ] as const,
    [perDayPaid, perDayFree],
  )

  const addonPlans = useMemo(
    () =>
      [
        {
          id: 13,
          price: 19,
          tierLabelKey: 'prices.tierSmall',
          lineKeys: ['prices.addonSmall.line1'] as const,
          pricingClass: 'pricing__item-label',
        },
        {
          id: 11,
          price: 49,
          tierLabelKey: 'prices.tierMedium',
          lineKeys: ['prices.addonMedium.line1'] as const,
          pricingClass: 'pricing__item-label',
        },
        {
          id: 12,
          price: 139,
          tierLabelKey: 'prices.tierLarge',
          lineKeys: ['prices.addonLarge.line1'] as const,
          pricingClass: 'pricing__item-label pricing__item-label-color1',
        },
      ] as const,
    [],
  )

  const Price = (props: {price: number | null; newPrice?: number}) => {
    const {price, newPrice} = props
    if (!newPrice) {
      if (price === null) {
        return <span style={{fontSize: '30px'}}>&nbsp;</span>
      }
      if (price === 0) {
        return <span style={{fontSize: '30px'}}>{t('prices.free')}</span>
      }
      return (
        <>
          {price} € <PerDayText>{t('prices.perDay')}</PerDayText>
        </>
      )
    }
    return (
      <div>
        <OldPrice>{price} €</OldPrice>
        <NewPrice>{newPrice} €</NewPrice>
      </div>
    )
  }

  const renderMainLines = (tier: MainTier) => {
    const n = MAIN_LINE_COUNT[tier]
    const keys = Array.from(
      {length: n},
      (_, i) => `prices.${tier}.line${i + 1}`,
    )
    return keys.map((key) => {
      const text = t(key)
      const display = text === '__SKIP__' ? t('prices.skipRow') : text
      return (
        <li key={key}>
          <i className="flaticon-comment"></i> {display}
        </li>
      )
    })
  }

  const renderUses = (tier: MainTier) => {
    const prefix =
      tier === 'paid'
        ? 'paidUses'
        : tier === 'free'
          ? 'freeUses'
          : 'charityUses'
    const n = USES_COUNT[tier]
    return Array.from({length: n}, (_, i) => (
      <li key={`${prefix}-${i}`}>
        <i className="flaticon-next"></i> {t(`prices.${prefix}.${i + 1}`)}
      </li>
    ))
  }

  return (
    <Page>
      <>
        <Helmet>
          <title>{t('prices.metaTitle')}</title>
          <meta name="description" content={t('prices.metaDescription')} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:title" content={t('prices.ogTitle')} />
          <meta property="og:description" content={t('prices.ogDescription')} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={t('prices.ogTitle')} />
          <meta
            name="twitter:description"
            content={t('prices.ogDescription')}
          />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        </Helmet>

        <section className="pricing__area dark_light_class pt-55 pb-55">
          <div className="container">
            <div className="section__title-wrapper mb-80">
              <span className="section__back-title">
                {t('prices.heroBack')}
              </span>
              <span className="section__subtitle">
                {t('prices.heroSubtitle')}
              </span>
              <h1 className="section__title">
                {t('prices.heroTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('prices.heroTitleHighlight')}
                </span>
              </h1>
            </div>

            <div className="row mb-55">
              {mainPlans.map((item) => (
                <div
                  key={item.id}
                  className="col-xxl-4 col-xl-4 col-lg-6 col-md-6"
                >
                  <div className="pricing__item mb-30">
                    <div className="pricing__item-header">
                      <StyledLabel className="pricing__item-lable">
                        <span className={item.pricingClass}>
                          {t(item.tierLabelKey)}
                        </span>
                        <PricingIcon />
                      </StyledLabel>
                      <CenteredPrice>
                        <span className="pricing__item-title">
                          <Price price={item.price} />
                        </span>
                      </CenteredPrice>
                    </div>

                    <div className="pricing__item-content">
                      <ul>{renderMainLines(item.tier)}</ul>
                    </div>

                    <div className="pricing__item-content mb-20">
                      <b>{t('prices.suitableFor')}</b>
                      <ul className="mt-20" style={{minHeight: 242}}>
                        {renderUses(item.tier)}
                        {item.tier === 'charity' && (
                          <li>
                            <center>
                              <p
                                style={{
                                  marginTop: '75px',
                                  fontWeight: 'bold',
                                }}
                              >
                                {t('prices.charityContact')}
                              </p>
                            </center>
                          </li>
                        )}
                      </ul>
                    </div>

                    <div className="pricing__btn-wrap">
                      <LocalLink
                        to={routes.organization.register}
                        className="pricing__btn"
                      >
                        {t('prices.choose')}
                      </LocalLink>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="section__title mb-35">
              {t('prices.addonsTitleBefore')}{' '}
              <span className="text__highlight">
                {t('prices.addonsTitleHighlight')}
              </span>
            </h2>
            <div className="row bd-theme-dark mb-55">
              {addonPlans.map((item) => (
                <div
                  key={item.id}
                  className="col-xxl-4 col-xl-4 col-lg-6 col-md-6"
                >
                  <div className="pricing__item mb-30">
                    <div className="pricing__item-header">
                      <div>
                        <span className="pricing__item-title">
                          {item.price} €
                        </span>
                      </div>
                      <div className="pricing__item-lable">
                        <span className={item.pricingClass}>
                          {t(item.tierLabelKey)}
                        </span>
                        <PricingIcon />
                      </div>
                    </div>

                    <div className="pricing__item-content">
                      <ul>
                        {item.lineKeys.map((key) => (
                          <li key={key}>
                            <i className="flaticon-comment"></i> {t(key)}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pricing__btn-wrap">
                      <LocalLink
                        to={routes.organization.register}
                        className="pricing__btn"
                      >
                        {t('prices.choose')}
                      </LocalLink>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    </Page>
  )
}

const OldPrice = styled.div`
  display: inline-block;
  color: #ff000087;
  text-decoration: none;
  position: relative;
  font-size: 30px;
  margin-right: 8px;

  &:before {
    content: ' ';
    display: block;
    width: 100%;
    border-top: 2px solid #ff0000c9;
    height: 18px;
    position: absolute;
    bottom: 0;
    left: 1px;
    transform: rotate(-13deg);
  }
`

const NewPrice = styled.div`
  display: inline-block;
  font-size: 40px;
`

const StyledLabel = styled.div`
  position: initial;
  inset-inline-end: auto;
  margin-bottom: 15px;

  & > span {
    width: 100%;
    clip-path: none;
    border-radius: 5px;
    margin-top: -15px;
    text-align: center;
    display: inline-block;
  }
`

const CenteredPrice = styled.div`
  text-align: center;
`

const PerDayText = styled.span`
  font-size: 1rem;
`
